import { MIN_CATEGORIES_WITH_PEERS, PeerCategoryKey } from '@/modules/shared/hr-form-engine';
import {
  AssessmentProfileStatus,
  ManagerPeerSelectionRecord,
  ManagerTransferActionType,
  PeerCategoryFormAssignment,
  PmReview,
  PmReviewDetail,
  ReviewData,
} from '../types/review-core.types';

export interface PeerAssignment {
  peerUserId: string;
  category: PeerCategoryKey;
}

export type PeerSelectionErrorKey =
  | 'MANAGER_ALREADY_FORWARDED'
  | 'AT_LEAST_ONE_SKILL_REQUIRED'
  | 'PEERS_MUST_SPAN_MIN_CATEGORIES';

export interface PeerSelectionPlan {
  /** Peer details this manager removed (only their own peers can be removed). */
  toDelete: PmReviewDetail[];
  /** Peers that have no detail yet and must be requested. */
  toInsert: PeerAssignment[];
  /** Distinct peer categories the review ends up with, across every manager. */
  categoryCount: number;
}

/**
 * Diffs the peer list a manager wants against the peer details that already exist for the review. Existing peers keep
 * their category (legacy behaviour: "already requested" peers are skipped), and category coverage is counted across
 * every manager's peers because a forwarded review can have peers chosen by several managers.
 */
export const planPeerSelection = (
  existingPeerDetails: PmReviewDetail[],
  desired: PeerAssignment[],
  managerId: string
): PeerSelectionPlan => {
  const desiredIds = new Set(desired.map((peer) => peer.peerUserId));
  const toDelete = existingPeerDetails.filter((detail) => detail.ReportingManagerId === managerId && !desiredIds.has(detail.PeerUserId));
  const deletedIds = new Set(toDelete.map((detail) => detail.PeerUserId));
  const existingIds = new Set(existingPeerDetails.map((detail) => detail.PeerUserId));

  const toInsert = desired.filter((peer) => !existingIds.has(peer.peerUserId));

  const categories = new Set<string>([
    ...existingPeerDetails.filter((detail) => !deletedIds.has(detail.PeerUserId)).map((detail) => detail.Category || 'Other'),
    ...toInsert.map((peer) => peer.category),
  ]);

  return { toDelete, toInsert, categoryCount: categories.size };
};

/** Validation shared by the dialog and the service so the rule cannot drift (legacy ProcessPmReviewDetails). */
export const validatePeerSelection = (input: {
  plan: PeerSelectionPlan;
  skills: string[];
  hasLockedSkills: boolean;
  managerSelection?: ManagerPeerSelectionRecord;
}): PeerSelectionErrorKey | null => {
  if (input.managerSelection?.SelectionStatus === 'Transferred') return 'MANAGER_ALREADY_FORWARDED';
  if (!input.hasLockedSkills && input.skills.filter((skill) => skill.trim()).length === 0) return 'AT_LEAST_ONE_SKILL_REQUIRED';
  if (input.plan.categoryCount < MIN_CATEGORIES_WITH_PEERS) return 'PEERS_MUST_SPAN_MIN_CATEGORIES';
  return null;
};

/** Skills are chosen once per review by whichever manager selects first, then locked for everyone after. */
export const resolveLockedPeerSkills = (peerData: ReviewData, submittedSkills: string[]): string[] =>
  peerData.PeerSkills.length > 0 ? peerData.PeerSkills : [...new Set(submittedSkills.map((skill) => skill.trim()).filter(Boolean))];

export const assignCategoryForm = (
  assignments: PeerCategoryFormAssignment[],
  next: PeerCategoryFormAssignment
): PeerCategoryFormAssignment[] =>
  assignments.some((assignment) => assignment.Category === next.Category) ? assignments : [...assignments, next];

const withSelectionStatus = (
  selections: ManagerPeerSelectionRecord[],
  managerId: string,
  status: AssessmentProfileStatus,
  completedDate: string | null = null
): ManagerPeerSelectionRecord[] =>
  selections.map((selection) =>
    selection.ManagerId === managerId ? { ...selection, SelectionStatus: status, CompletedDate: completedDate } : selection
  );

/** Legacy AddOrActivateManager: keeps ReportingManagerIds and ManagerPeerSelections in sync in one place. */
export const addOrActivateManager = (
  peerData: ReviewData,
  managerId: string,
  status: AssessmentProfileStatus
): ReviewData => {
  const hasSelection = peerData.ManagerPeerSelections.some((selection) => selection.ManagerId === managerId);
  return {
    ...peerData,
    ReportingManagerIds: peerData.ReportingManagerIds.includes(managerId)
      ? peerData.ReportingManagerIds
      : [...peerData.ReportingManagerIds, managerId],
    ManagerPeerSelections: hasSelection
      ? withSelectionStatus(peerData.ManagerPeerSelections, managerId, status)
      : [...peerData.ManagerPeerSelections, { ManagerId: managerId, SelectionStatus: status, CompletedDate: null }],
  };
};

/**
 * Legacy GetManagerLineage: walks TransferHistory backwards from a manager to the managers it received the review
 * from. Scoping to the manager's own chain is what tells a "revert" (handing back up your own chain) from a
 * "transfer" (handing to someone new to your chain).
 */
export const getManagerLineage = (peerData: ReviewData, managerId: string): Set<string> => {
  const lineage = new Set<string>([managerId]);
  let currentId = managerId;

  for (;;) {
    const incoming = peerData.TransferHistory.filter((record) => record.ManagerId.toLowerCase() === currentId.toLowerCase()).sort(
      (a, b) => b.ActionDate.localeCompare(a.ActionDate)
    )[0];
    if (!incoming || lineage.has(incoming.FromManagerId)) break;
    lineage.add(incoming.FromManagerId);
    currentId = incoming.FromManagerId;
  }
  return lineage;
};

/** Legacy ForwardPeerReview: hands the peer selection of a review over to other managers. */
export const applyPeerForward = (
  peerData: ReviewData,
  input: { fromManagerId: string; toManagerIds: string[]; deadline?: string | null; now?: string }
): ReviewData => {
  const now = input.now ?? new Date().toISOString();
  const targets = [...new Set(input.toManagerIds.filter(Boolean))];
  const lineage = getManagerLineage(peerData, input.fromManagerId);
  const deadline = input.deadline ?? peerData.ReportingManagerDeadLine ?? null;

  const lastActive = peerData.TransferHistory.filter(
    (record) => record.ManagerId === input.fromManagerId && record.Status === 'Pending'
  ).sort((a, b) => b.ActionDate.localeCompare(a.ActionDate))[0];

  let next: ReviewData = {
    ...peerData,
    TransferHistory: peerData.TransferHistory.map((record) => (record === lastActive ? { ...record, Status: 'Transferred' } : record)),
  };

  targets.forEach((targetId) => {
    const actionType: ManagerTransferActionType = lineage.has(targetId) ? 'Revert' : 'Transfer';
    next = {
      ...next,
      TransferHistory: [
        ...next.TransferHistory,
        { ManagerId: targetId, FromManagerId: input.fromManagerId, ActionType: actionType, ActionDate: now, Status: 'Pending', Deadline: deadline },
      ],
      ManagerPeerSelections: withSelectionStatus(next.ManagerPeerSelections, input.fromManagerId, 'Transferred'),
    };
    next = addOrActivateManager(next, targetId, 'Pending');
  });

  return next;
};

export interface PeerSelectionOutcome {
  peerData: ReviewData;
  peerList: PmReview['PeerList'];
  peerSelectedStatus: AssessmentProfileStatus;
}

/**
 * Legacy UpdatePmReviewPeerStatusAsync: recomputes the review's peer step after a manager saved peers. The peer list
 * is completed only when every requested peer has submitted; the review-wide selection is completed once no manager
 * is still pending.
 */
export const computePeerSelectionOutcome = (input: {
  review: PmReview;
  peerData: ReviewData;
  peerDetailsAfter: PmReviewDetail[];
  managerId: string;
  now?: string;
}): PeerSelectionOutcome => {
  const now = input.now ?? new Date().toISOString();
  const { peerDetailsAfter, managerId } = input;

  const peerList: PmReview['PeerList'] = peerDetailsAfter.length
    ? peerDetailsAfter.every((detail) => detail.Status === 'Completed')
      ? 'IsCompleted'
      : 'IsForwarded'
    : input.review.PeerList;

  if (!managerId) return { peerData: input.peerData, peerList, peerSelectedStatus: 'Completed' };

  const selections = withSelectionStatus(input.peerData.ManagerPeerSelections, managerId, 'Completed', now);
  return {
    peerData: { ...input.peerData, ManagerPeerSelections: selections },
    peerList,
    peerSelectedStatus: selections.some((selection) => selection.SelectionStatus === 'Pending') ? 'Pending' : 'Completed',
  };
};

/**
 * Legacy ResolvePeerReviewStatusAsync + ApplyReviewStatusToField: after a reviewer submits, a peer step is completed
 * only when all peers finished; every other review type completes on its single submission.
 */
export const computeStepStatusAfterSubmit = (
  reviewType: string,
  peerDetails: PmReviewDetail[],
  submittedDetailId: string
): PmReview['PeerList'] => {
  if (reviewType !== 'peer') return 'IsCompleted';
  return peerDetails.every((detail) => detail.ItemId === submittedDetailId || detail.Status === 'Completed') ? 'IsCompleted' : 'IsForwarded';
};
